/** A tiny dependency-free control panel bound to the settings store. */

import type { Store } from '../core/state';

export interface ControlHandle {
  refresh(): void;
  readonly row: HTMLElement;
  setVisible(visible: boolean): void;
}

export interface SliderHandle extends ControlHandle {
  setRange(min: number, max: number, step?: number): void;
}

export interface SelectHandle extends ControlHandle {
  setOptions(options: OptionList): void;
}

export interface CodeHandle extends ControlHandle {
  setError(message: string | null): void;
  getValue(): string;
}

export interface ReadoutHandle {
  set(html: string): void;
  readonly row: HTMLElement;
}

export type OptionList = Array<{ value: string | number; label: string }> | string[];

export interface SliderOptions {
  min: number;
  max: number;
  step?: number;
  format?: (value: number) => string;
}

function normalizeOptions(options: OptionList): Array<{ value: string | number; label: string }> {
  return options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

function defaultFormat(step: number): (value: number) => string {
  if (step >= 1) return (v) => String(Math.round(v));
  const digits = Math.min(4, Math.max(1, Math.ceil(-Math.log10(step))));
  return (v) => v.toFixed(digits);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (parent) parent.appendChild(node);
  return node;
}

export class Folder {
  readonly element: HTMLElement;
  readonly body: HTMLElement;

  constructor(
    private readonly panel: Panel,
    private readonly store: Store,
    title: string,
    open: boolean,
  ) {
    this.element = el('div', 'folder');
    if (!open) this.element.classList.add('closed');

    const head = el('div', 'folder-head', this.element);
    const caret = el('span', 'folder-caret', head);
    caret.textContent = '\u25B6';
    const label = el('span', undefined, head);
    label.textContent = title;
    head.addEventListener('click', () => this.element.classList.toggle('closed'));

    this.body = el('div', 'folder-body', this.element);
  }

  private makeRow(label: string, wide = false): { row: HTMLElement; ctrl: HTMLElement } {
    const row = el('div', wide ? 'row wide' : 'row', this.body);
    const labelEl = el('label', undefined, row);
    labelEl.textContent = label;
    const ctrl = el('div', 'ctrl', row);
    return { row, ctrl };
  }

  slider(label: string, path: string, options: SliderOptions): SliderHandle {
    const { row, ctrl } = this.makeRow(label);
    const step = options.step ?? (options.max - options.min) / 100;
    let format = options.format ?? defaultFormat(step);

    const range = el('input', undefined, ctrl);
    range.type = 'range';
    range.min = String(options.min);
    range.max = String(options.max);
    range.step = String(step);

    const num = el('input', 'num', ctrl);
    num.type = 'text';
    num.inputMode = 'decimal';

    const commit = (value: number) => {
      if (!Number.isFinite(value)) return;
      const clamped = Math.min(Number(range.max), Math.max(Number(range.min), value));
      this.store.set(path, clamped);
      handle.refresh();
    };

    range.addEventListener('input', () => commit(Number(range.value)));
    num.addEventListener('change', () => commit(Number(num.value)));
    num.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') commit(Number(num.value));
    });

    const handle: SliderHandle = {
      row,
      refresh: () => {
        const value = Number(this.store.get(path) ?? 0);
        range.value = String(value);
        if (document.activeElement !== num) num.value = format(value);
      },
      setRange: (min, max, nextStep) => {
        range.min = String(min);
        range.max = String(max);
        if (nextStep !== undefined) {
          range.step = String(nextStep);
          if (!options.format) format = defaultFormat(nextStep);
        }
        handle.refresh();
      },
      setVisible: (visible) => {
        row.style.display = visible ? '' : 'none';
      },
    };

    this.panel.register(handle);
    return handle;
  }

  select(label: string, path: string, options: OptionList): SelectHandle {
    const { row, ctrl } = this.makeRow(label);
    const select = el('select', undefined, ctrl);
    let entries = normalizeOptions(options);

    const fill = () => {
      select.textContent = '';
      for (const entry of entries) {
        const option = el('option', undefined, select);
        option.value = String(entry.value);
        option.textContent = entry.label;
      }
    };
    fill();

    select.addEventListener('change', () => {
      const entry = entries.find((e) => String(e.value) === select.value);
      this.store.set(path, entry ? entry.value : select.value);
    });

    const handle: SelectHandle = {
      row,
      refresh: () => {
        select.value = String(this.store.get(path) ?? '');
      },
      setOptions: (next) => {
        entries = normalizeOptions(next);
        fill();
        handle.refresh();
      },
      setVisible: (visible) => {
        row.style.display = visible ? '' : 'none';
      },
    };

    this.panel.register(handle);
    return handle;
  }

  toggle(label: string, path: string): ControlHandle {
    const { row, ctrl } = this.makeRow(label);
    const button = el('button', 'toggle', ctrl);
    button.type = 'button';
    button.addEventListener('click', () => {
      this.store.set(path, !this.store.get<boolean>(path));
      handle.refresh();
    });

    const handle: ControlHandle = {
      row,
      refresh: () => {
        button.classList.toggle('on', !!this.store.get<boolean>(path));
      },
      setVisible: (visible) => {
        row.style.display = visible ? '' : 'none';
      },
    };

    this.panel.register(handle);
    return handle;
  }

  color(label: string, path: string): ControlHandle {
    const { row, ctrl } = this.makeRow(label);
    const input = el('input', undefined, ctrl);
    input.type = 'color';
    input.style.flex = '0 0 34px';
    input.style.height = '18px';
    input.style.padding = '0';
    input.style.border = '1px solid var(--panel-border)';
    input.style.background = 'transparent';
    input.style.cursor = 'pointer';
    input.addEventListener('input', () => this.store.set(path, input.value));

    const handle: ControlHandle = {
      row,
      refresh: () => {
        input.value = String(this.store.get(path) ?? '#000000');
      },
      setVisible: (visible) => {
        row.style.display = visible ? '' : 'none';
      },
    };

    this.panel.register(handle);
    return handle;
  }

  buttons(items: Array<{ label: string; onClick: () => void; primary?: boolean }>): HTMLElement[] {
    const group = el('div', 'btn-group', this.body);
    return items.map((item) => {
      const button = el('button', item.primary ? 'btn primary' : 'btn', group);
      button.type = 'button';
      button.textContent = item.label;
      button.addEventListener('click', item.onClick);
      return button;
    });
  }

  button(label: string, onClick: () => void, primary = false): HTMLElement {
    return this.buttons([{ label, onClick, primary }])[0];
  }

  note(html: string): { set(html: string): void } {
    const note = el('p', 'note', this.body);
    note.innerHTML = html;
    return { set: (next: string) => (note.innerHTML = next) };
  }

  readout(): ReadoutHandle {
    const row = el('div', 'readout', this.body);
    return { row, set: (html: string) => (row.innerHTML = html) };
  }

  code(label: string, path: string, onApply: (source: string) => void): CodeHandle {
    const { row } = this.makeRow(label, true);
    const area = el('textarea', undefined, row);
    area.spellcheck = false;
    area.value = String(this.store.get(path) ?? '');

    const error = el('p', 'error', row);
    error.style.display = 'none';

    const group = el('div', 'btn-group', row);
    const apply = el('button', 'btn primary', group);
    apply.type = 'button';
    apply.textContent = 'Apply (\u2318\u21B5)';
    const revert = el('button', 'btn', group);
    revert.type = 'button';
    revert.textContent = 'Revert';

    const doApply = () => onApply(area.value);
    apply.addEventListener('click', doApply);
    revert.addEventListener('click', () => {
      area.value = String(this.store.get(path) ?? '');
      doApply();
    });
    area.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        doApply();
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        const start = area.selectionStart;
        area.setRangeText('  ', start, area.selectionEnd, 'end');
      }
      e.stopPropagation();
    });

    const handle: CodeHandle = {
      row,
      refresh: () => {
        if (document.activeElement !== area) {
          area.value = String(this.store.get(path) ?? '');
        }
      },
      setError: (message) => {
        error.style.display = message ? '' : 'none';
        error.textContent = message ?? '';
      },
      getValue: () => area.value,
      setVisible: (visible) => {
        row.style.display = visible ? '' : 'none';
      },
    };

    this.panel.register(handle);
    return handle;
  }
}

export class Panel {
  private readonly controls: ControlHandle[] = [];
  private refreshScheduled = false;

  constructor(
    private readonly root: HTMLElement,
    private readonly store: Store,
  ) {
    this.store.onChange(() => this.scheduleRefresh());
  }

  folder(title: string, open = true): Folder {
    const folder = new Folder(this, this.store, title, open);
    this.root.appendChild(folder.element);
    return folder;
  }

  register(handle: ControlHandle): void {
    this.controls.push(handle);
    handle.refresh();
  }

  refresh(): void {
    for (const control of this.controls) control.refresh();
  }

  private scheduleRefresh(): void {
    if (this.refreshScheduled) return;
    this.refreshScheduled = true;
    requestAnimationFrame(() => {
      this.refreshScheduled = false;
      this.refresh();
    });
  }
}
